import { useCallback, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Upload } from "lucide-react";
import {
  autoMapPatientColumns,
  PATIENT_DB_FIELDS,
  type PatientNameFormat,
  type DuplicateStrategy,
  type PatientImportResult,
} from "@bookdental/schemas";
import { Button, toast } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { FileDropzone } from "./FileDropzone";
import { Stepper } from "./Stepper";
import { MappingTable } from "./MappingTable";
import { ImportResultView } from "./ImportResultView";
import { ImportHistoryList } from "./ImportHistoryList";
import {
  ComposeAddressPanel,
  DuplicatePanel,
  NameFormatPanel,
  PreImportSummary,
} from "./PatientImportPanels";
import { parseImportFile, previewRows, type ParsedFile } from "./lib/parseFile";
import { buildPatientRecords } from "./lib/buildPatientRecords";
import {
  ErroImportacaoParcial,
  importarEmLotes,
  type ProgressoImport,
} from "./lib/chunked-import";

const STEPS = ["Arquivo", "Mapeamento", "Importar"];
type Step = 0 | 1 | 2;

export function ImportPatientsSection() {
  const [step, setStep] = useState<Step>(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [addressCols, setAddressCols] = useState<string[]>([]);
  const [nameFormat, setNameFormat] = useState<PatientNameFormat>("titlecase");
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>("skip");
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState<PatientImportResult | null>(null);
  const [progresso, setProgresso] = useState<ProgressoImport | null>(null);

  const importMutation = trpc.import.patients.useMutation();
  const utils = trpc.useUtils();

  const sampleRow = useMemo(
    () => (parsed ? previewRows(parsed, 1)[0] : undefined),
    [parsed],
  );
  const mappedCount = Object.values(mappings).filter((v) => v && v !== "ignore").length;
  const isNameMapped = Object.values(mappings).includes("name");
  const isAddressMapped = Object.values(mappings).includes("address");
  const unmappedColumns = useMemo(() => {
    if (!parsed) return [];
    return parsed.headers.filter((h) => !mappings[h] || mappings[h] === "ignore");
  }, [parsed, mappings]);

  const reset = useCallback(() => {
    setStep(0);
    setFile(null);
    setParsed(null);
    setMappings({});
    setAddressCols([]);
    setNameFormat("titlecase");
    setDuplicateStrategy("skip");
    setResult(null);
  }, []);

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    try {
      const p = await parseImportFile(file);
      setParsed(p);
      setMappings(autoMapPatientColumns(p.headers));
      setStep(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler arquivo");
    } finally {
      setParsing(false);
    }
  }, [file]);

  const handleImport = useCallback(async () => {
    if (!parsed) return;
    const records = buildPatientRecords(parsed, {
      mappings,
      nameFormat,
      addressColumns: addressCols,
    });
    if (records.length === 0) {
      toast.error("Nenhum registro valido pra importar.");
      return;
    }
    try {
      const res = await importarEmLotes(
        records,
        (lote) =>
          importMutation.mutateAsync({ records: lote, duplicateStrategy, fileName: file?.name }),
        setProgresso,
      );
      setResult(res);
      utils.patient.list.invalidate();
      utils.import.listBatches.invalidate();
      toast.success(`Importacao concluida: ${res.created} criados.`);
    } catch (err) {
      // Falha no meio de uma planilha grande: o que já entrou continua no banco. Dizer isso evita
      // que o usuário reimporte o arquivo inteiro e duplique tudo que deu certo.
      if (err instanceof ErroImportacaoParcial && err.parcial) {
        setResult(err.parcial as PatientImportResult);
        utils.patient.list.invalidate();
        utils.import.listBatches.invalidate();
        toast.error(
          `Importacao interrompida apos ${err.parcial.totalProcessed} registros. ` +
            `Os ja importados foram mantidos — reenvie so' o restante da planilha.`,
        );
        return;
      }
      toast.error(err instanceof Error ? err.message : "Erro na importacao");
    } finally {
      setProgresso(null);
    }
  }, [parsed, mappings, nameFormat, addressCols, duplicateStrategy, importMutation, utils, file]);

  const busy = parsing || importMutation.isPending || progresso !== null;

  return (
    <div className="space-y-8">
      <section className="space-y-5">
        <SectionHeader
          title="Importacao"
          subtitle="Envie um arquivo CSV ou Excel pra importar seus pacientes."
        />
        <Stepper steps={STEPS} current={step} />

      {step === 0 && (
        <div className="space-y-4">
          <div data-tour="config-importar-arquivo">
            <FileDropzone file={file} onFile={setFile} disabled={busy} />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleParse} disabled={!file || busy}>
              {parsing ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
              )}
              {parsing ? "Lendo..." : "Proximo"}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && parsed && (
        <div className="space-y-4">
          <MappingTable
            headers={parsed.headers}
            sampleRow={sampleRow}
            dbFields={PATIENT_DB_FIELDS}
            mappings={mappings}
            onChange={(col, value) =>
              setMappings((prev) => {
                const next = { ...prev };
                if (!value || value === "ignore") delete next[col];
                else next[col] = value;
                return next;
              })
            }
            requiredField="name"
            requiredLabel="Nome"
            totalRows={parsed.totalRows}
          />

          {unmappedColumns.length > 0 && !isAddressMapped && (
            <ComposeAddressPanel
              columns={unmappedColumns}
              selected={addressCols}
              onChange={setAddressCols}
            />
          )}

          {isNameMapped && (
            <NameFormatPanel value={nameFormat} onChange={setNameFormat} />
          )}

          <DuplicatePanel value={duplicateStrategy} onChange={setDuplicateStrategy} />

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(0)}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Voltar
            </Button>
            <Button onClick={() => setStep(2)} disabled={!isNameMapped}>
              <ArrowRight className="mr-1.5 h-3.5 w-3.5" /> Proximo
            </Button>
          </div>
        </div>
      )}

      {step === 2 && parsed && !result && (
        <div className="space-y-4">
          <PreImportSummary
            total={parsed.totalRows}
            mapped={mappedCount}
            addressCount={addressCols.length}
            nameFormat={nameFormat}
            duplicateStrategy={duplicateStrategy}
          />
          {/* Planilha grande vai em lotes — sem isso o usuário fica minutos olhando um spinner. */}
          {progresso && progresso.lotes > 1 && (
            <div className="space-y-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5">
              <p className="text-xs text-zinc-600">
                Enviando lote {progresso.lote} de {progresso.lotes} — {progresso.enviados} de{" "}
                {progresso.total} registros
              </p>
              <div className="h-1.5 overflow-hidden rounded-full bg-zinc-200">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{ width: `${Math.round((progresso.enviados / progresso.total) * 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} disabled={busy}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Voltar
            </Button>
            <Button onClick={handleImport} disabled={busy}>
              {busy ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1.5 h-3.5 w-3.5" />
              )}
              {busy ? "Importando..." : "Importar agora"}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && result && (
        <div className="space-y-4">
          <ImportResultView
            totalProcessed={result.totalProcessed}
            chips={[
              { label: `${result.created} criados`, tone: "success" },
              ...(result.updated > 0
                ? [{ label: `${result.updated} atualizados`, tone: "info" as const }]
                : []),
              ...(result.skipped > 0
                ? [{ label: `${result.skipped} duplicatas puladas`, tone: "warning" as const }]
                : []),
              ...(result.errors > 0
                ? [{ label: `${result.errors} erros`, tone: "error" as const }]
                : []),
            ]}
            errorDetails={result.errorDetails}
          />
          <div className="flex justify-center">
            <Button variant="outline" onClick={reset}>
              Nova importacao
            </Button>
          </div>
        </div>
      )}
      </section>

      <section className="space-y-3">
        <SectionHeader
          title="Importacoes realizadas"
          subtitle="Historico das importacoes feitas. Use 'Apagar' pra remover os registros criados."
        />
        <ImportHistoryList type="PATIENTS" />
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="border-b border-zinc-100 pb-2">
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>
    </div>
  );
}
